import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from './Header';
import './AnnotatorJobEditor.css';

function AnnotatorJobEditor() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [job, setJob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcriptText, setTranscriptText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  const audioRef = useRef(null);

  // Fetch job details
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    const fetchJob = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/corrections/annotator/job/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setJob(data.request);

          // Set audio URL if available
          if (data.audioUrl) {
            setAudioUrl(data.audioUrl);
            console.log('Audio URL available for playback');
          }

          // Initialize transcript text from original data (handle both camelCase and snake_case)
          const originalData = data.request.originalTranscriptData || data.request.original_transcript_data;
          if (originalData) {
            const text = extractTextFromTranscript(originalData);
            setTranscriptText(text);
          }
        } else if (response.status === 403) {
          setError('Access denied. This job is not assigned to you.');
        } else {
          setError('Failed to load job');
        }
      } catch (err) {
        console.error('Error fetching job:', err);
        setError('Failed to load job');
      } finally {
        setIsLoading(false);
      }
    };

    fetchJob();
  }, [jobId, isAuthenticated, authLoading]);

  // Extract text from transcript data
  const extractTextFromTranscript = (data) => {
    if (!data) return '';

    try {
      // Handle different transcript formats
      if (data.segments) {
        return data.segments
          .map(seg => seg.text || '')
          .join('\n\n')
          .trim();
      } else if (data.text) {
        return data.text;
      } else if (typeof data === 'string') {
        return data;
      }
      return JSON.stringify(data, null, 2);
    } catch (err) {
      console.error('Error extracting text:', err);
      return '';
    }
  };

  // Submit corrected transcript
  const handleSubmit = async () => {
    if (!transcriptText.trim()) {
      alert('Transcript cannot be empty');
      return;
    }

    setShowConfirmDialog(false);
    setIsSaving(true);

    try {
      // Reconstruct transcript data with corrections
      const originalData = job.originalTranscriptData || job.original_transcript_data || {};
      const correctedData = {
        ...originalData,
        text: transcriptText,
        corrected: true,
        correctedAt: new Date().toISOString()
      };

      const response = await fetch(`/api/corrections/annotator/job/${jobId}/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          correctedTranscriptData: correctedData
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Check if folder and transcript were created
        if (data.folderCreated && data.transcriptCreated) {
          alert(`Job submitted successfully!\n\n✅ Folder: "${data.folderName}"\n✅ Transcript saved\n✅ User notification: ${data.notifications?.userNotified ? 'Sent' : 'Failed'}\n✅ Admin notification: ${data.notifications?.adminNotified ? 'Sent' : 'Failed'}`);
        } else {
          alert(`Job submitted but with issues:\n\nFolder created: ${data.folderCreated ? 'Yes' : 'No'}\nTranscript saved: ${data.transcriptCreated ? 'Yes' : 'No'}\nUser notified: ${data.notifications?.userNotified ? 'Yes' : 'No'}\n\nPlease contact admin if there are issues.`);
        }
        navigate('/annotator/dashboard');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to submit job');
      }
    } catch (err) {
      console.error('Error submitting job:', err);
      alert('Failed to submit job');
    } finally {
      setIsSaving(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || isLoading) {
    return (
      <div className="job-editor">
        <Header />
        <main className="editor-main">
          <div className="loading">Loading job...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-editor">
        <Header />
        <main className="editor-main">
          <div className="error-container">
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/annotator/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-editor">
        <Header />
        <main className="editor-main">
          <div className="loading">Job not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="job-editor">
      <Header />
      <main className="editor-main">
        <div className="editor-container">
          {/* Header */}
          <div className="editor-header">
            <div className="header-left">
              <button
                className="back-btn"
                onClick={() => navigate('/annotator/dashboard')}
              >
                ← Back to Dashboard
              </button>
              <h1>Edit Job #{job.id}</h1>
            </div>
            <div className="header-right">
              <button
                className="submit-btn"
                onClick={() => setShowConfirmDialog(true)}
                disabled={isSaving || !transcriptText.trim()}
              >
                {isSaving ? 'Submitting...' : 'Submit Correction'}
              </button>
            </div>
          </div>

          {/* Job Info */}
          <div className="job-info">
            <div className="info-item">
              <span className="info-label">User:</span>
              <span className="info-value">{job.userEmail || job.user_email || 'Unknown'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Assigned:</span>
              <span className="info-value">{formatDate(job.assignedAt || job.assigned_at)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Payment:</span>
              <span className="info-value">${job.cost || job.costUsd || job.cost_usd || '0.00'}</span>
            </div>
          </div>

          {/* Instructions */}
          <div className="instructions">
            <h3>Instructions:</h3>
            <ul>
              <li>Listen to the audio and correct any transcription errors</li>
              <li>Fix spelling, grammar, and punctuation</li>
              <li>Verify speaker labels if applicable</li>
              <li>Submit when complete - the user will be notified via email</li>
            </ul>
          </div>

          {/* Audio Player */}
          {audioUrl ? (
            <div className="audio-player-section">
              <h3>Audio Playback</h3>
              <div className="audio-controls">
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  controls
                  className="audio-player"
                  onRateChange={(e) => setPlaybackRate(e.target.playbackRate)}
                />
                <div className="playback-controls">
                  <label>Speed: </label>
                  <select
                    value={playbackRate}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value);
                      setPlaybackRate(rate);
                      if (audioRef.current) {
                        audioRef.current.playbackRate = rate;
                      }
                    }}
                  >
                    <option value="0.5">0.5x</option>
                    <option value="0.75">0.75x</option>
                    <option value="1">1x</option>
                    <option value="1.25">1.25x</option>
                    <option value="1.5">1.5x</option>
                    <option value="2">2x</option>
                  </select>
                  <button
                    className="skip-btn"
                    onClick={() => {
                      if (audioRef.current) {
                        audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 5);
                      }
                    }}
                  >
                    -5s
                  </button>
                  <button
                    className="skip-btn"
                    onClick={() => {
                      if (audioRef.current) {
                        audioRef.current.currentTime += 5;
                      }
                    }}
                  >
                    +5s
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="audio-player-section no-audio">
              <h3>Audio Playback</h3>
              <p>No audio available for this job.</p>
            </div>
          )}

          {/* Editor */}
          <div className="editor-section">
            <h3>Transcript Editor</h3>
            <textarea
              className="transcript-textarea"
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              placeholder="Transcript text..."
              spellCheck="true"
            />
            <div className="editor-stats">
              <span>{transcriptText.length} characters</span>
              <span>{transcriptText.split(/\s+/).filter(w => w).length} words</span>
            </div>
          </div>

          {/* Original Transcript Reference */}
          <details className="original-transcript">
            <summary>View Original Transcript (Reference)</summary>
            <pre>{extractTextFromTranscript(job.originalTranscriptData || job.original_transcript_data)}</pre>
          </details>
        </div>
      </main>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Submit Corrected Transcript?</h3>
            <p>
              Once you submit this job, the corrected transcript will be sent to the user
              and the job will be marked as complete. You will receive payment for this job.
            </p>
            <p>
              <strong>Are you sure the transcript is ready to submit?</strong>
            </p>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? 'Submitting...' : 'Yes, Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnotatorJobEditor;
